"use client";

import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { FormEvent, useEffect, useRef, useState } from "react";
import {
  Check,
  Headphones,
  Loader2,
  LogIn,
  Send,
  ShieldCheck,
  X,
} from "lucide-react";

import { ROUTES } from "@/src/constants/routes";
import { cn } from "@/src/lib/utils/cn";
import { useAppSelector } from "@/src/store/hooks";

const SUPPORT_WHATSAPP_NUMBER = "201144144800";
const ROBOT_IMAGE_SRC = "/Office Furniture/repot.png";
const BUBBLE_MESSAGES = ["تواصل مع خدمة العملاء", "كيف يمكننا خدمتك؟"];
const WHATSAPP_MESSAGE_PREFIX = "مرحبًا، لدي استفسار لخدمة العملاء:\n\n";

type SubmissionState = "idle" | "loading" | "success";

function SupportBubble({
  message,
  isVisible,
  onOpen,
}: {
  message: string;
  isVisible: boolean;
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        "absolute right-[calc(100%+0.35rem)] bottom-11 w-max max-w-[calc(100vw-7rem)] whitespace-nowrap rounded-full border border-[#ee376a]/10 bg-white px-3.5 py-2 text-xs font-bold text-[var(--color-ink)] shadow-[0_14px_34px_rgba(238,55,106,0.18)] outline-none transition-all duration-500 focus-visible:ring-2 focus-visible:ring-[#ee376a] focus-visible:ring-offset-2 sm:bottom-[calc(100%+0.35rem)] sm:right-1 sm:max-w-[min(16rem,calc(100vw-2rem))] sm:rounded-2xl sm:px-4 sm:py-2.5 sm:text-sm",
        "before:absolute before:top-1/2 before:-right-1.5 before:h-3 before:w-3 before:-translate-y-1/2 before:rotate-45 before:border-r before:border-t before:border-[#ee376a]/10 before:bg-white sm:before:top-auto sm:before:-bottom-1.5 sm:before:right-8 sm:before:translate-y-0 sm:before:border-b sm:before:border-r sm:before:border-t-0",
        isVisible
          ? "translate-y-0 scale-100 opacity-100"
          : "pointer-events-none -translate-x-6 scale-95 opacity-0",
      )}
      aria-hidden={!isVisible}
      tabIndex={isVisible ? 0 : -1}
    >
      {message}
    </button>
  );
}

function SupportSuccess() {
  return (
    <div
      className="flex min-h-[15rem] flex-col items-center justify-center px-3 py-5 text-center"
      role="status"
      aria-live="polite"
    >
      <div className="support-checkmark mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 shadow-[0_14px_30px_rgba(16,185,129,0.16)]">
        <Check className="h-8 w-8" aria-hidden="true" />
      </div>
      <p className="text-base font-extrabold text-[var(--color-ink)]">
        تم تجهيز رسالتك بنجاح ✓
      </p>
      <p className="mt-2 max-w-[17rem] text-sm leading-6 text-[var(--color-ink-soft)]">
        تم فتح واتساب، اضغط إرسال لإرسال استفسارك إلى خدمة العملاء.
      </p>
    </div>
  );
}
function SupportLoginPrompt({ onLoginClick }: { onLoginClick: () => void }) {
  return (
    <div className="px-4 py-5 text-center" role="status" aria-live="polite">
      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#fff0f5] text-[#ee376a] shadow-[0_14px_28px_rgba(238,55,106,0.16)]">
        <ShieldCheck className="h-7 w-7" aria-hidden="true" />
      </div>
      <p className="text-base font-extrabold text-[var(--color-ink)]">
        سجّل دخولك للتواصل مع خدمة العملاء
      </p>
      <p className="mx-auto mt-2 max-w-[17.5rem] text-sm leading-6 text-[var(--color-ink-soft)]">
        يرجى تسجيل الدخول أولًا ثم إرسال استفسارك مباشرة إلى فريق خدمة العملاء.
      </p>
      <Link
        href={ROUTES.LOGIN}
        onClick={onLoginClick}
        className="mt-5 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-2xl !bg-[#ee376a] px-4 py-3 text-sm font-extrabold !text-white shadow-[0_14px_28px_rgba(238,55,106,0.24)] outline-none hover:-translate-y-0.5 hover:!bg-[#db285b] active:!bg-[#c92150] focus-visible:!bg-[#ee376a] focus-visible:ring-2 focus-visible:ring-[#ee376a] focus-visible:ring-offset-2"
      >
        <LogIn className="h-4 w-4" aria-hidden="true" />
        تسجيل الدخول الآن
      </Link>
    </div>
  );
}
export default function SupportWidget() {
  const pathname = usePathname();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [submissionState, setSubmissionState] =
    useState<SubmissionState>("idle");
  const [isBubbleVisible, setIsBubbleVisible] = useState(false);
  const [bubbleIndex, setBubbleIndex] = useState(0);
  const widgetRef = useRef<HTMLDivElement>(null);
  const successTimerRef = useRef<number | null>(null);

  const closeForm = () => {
    setIsOpen(false);
    setError("");
    setSubmissionState("idle");
  };

  useEffect(() => {
    if (isOpen) {
      return;
    }

    let cycleTimer: number;
    let hideTimer: number;

    const showBubble = () => {
      setBubbleIndex((current) => (current + 1) % BUBBLE_MESSAGES.length);
      setIsBubbleVisible(true);
      hideTimer = window.setTimeout(() => {
        setIsBubbleVisible(false);
      }, 6500);
      cycleTimer = window.setTimeout(showBubble, 30000);
    };

    cycleTimer = window.setTimeout(showBubble, 1000);

    return () => {
      window.clearTimeout(cycleTimer);
      window.clearTimeout(hideTimer);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeForm();
      }
    };

    const handlePointerDown = (event: PointerEvent) => {
      if (
        widgetRef.current &&
        !widgetRef.current.contains(event.target as Node)
      ) {
        closeForm();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("pointerdown", handlePointerDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [isOpen]);

  useEffect(() => {
    const frameId = window.requestAnimationFrame(() => {
      setIsOpen(false);
      setError("");
      setSubmissionState("idle");
      setIsBubbleVisible(false);
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [pathname]);

  useEffect(() => {
    return () => {
      if (successTimerRef.current) {
        window.clearTimeout(successTimerRef.current);
      }
    };
  }, []);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!isAuthenticated) {
      return;
    }

    const trimmedMessage = message.trim();

    if (!trimmedMessage) {
      setError("يرجى كتابة استفسارك أولاً");
      return;
    }

    setError("");
    setSubmissionState("loading");

    const encodedMessage = encodeURIComponent(
      `${WHATSAPP_MESSAGE_PREFIX}${trimmedMessage}`,
    );
    const whatsappUrl = `https://wa.me/${SUPPORT_WHATSAPP_NUMBER}?text=${encodedMessage}`;

    window.open(whatsappUrl, "_blank", "noopener,noreferrer");

    window.setTimeout(() => {
      setSubmissionState("success");
      setMessage("");

      successTimerRef.current = window.setTimeout(() => {
        setSubmissionState("idle");
        setIsOpen(false);
      }, 2600);
    }, 300);
  };

  return (
    <div
      ref={widgetRef}
      className="fixed bottom-[calc(5.5rem+env(safe-area-inset-bottom))] right-4 z-[60] flex flex-col items-end sm:right-5 lg:bottom-7 lg:right-7"
      dir="rtl"
    >
      <div
        className={cn(
          "absolute bottom-[calc(100%+0.9rem)] right-0 w-[min(21.5rem,calc(100vw-2rem))] origin-bottom-right overflow-hidden rounded-[1.35rem] border border-[#ee376a]/12 bg-white/95 text-right shadow-[0_20px_70px_rgba(36,36,36,0.16)] backdrop-blur-xl transition-all duration-300",
          isOpen
            ? "pointer-events-auto translate-y-0 scale-100 opacity-100"
            : "pointer-events-none translate-y-3 scale-95 opacity-0",
        )}
        aria-hidden={!isOpen}
      >
        <div className="border-b border-[#ee376a]/10 bg-[#fff5f8] px-4 py-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-2">
              <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#ee376a] text-white shadow-[0_10px_22px_rgba(238,55,106,0.24)]">
                <Headphones className="h-4.5 w-4.5" aria-hidden="true" />
              </span>
              <div>
                <h2 className="text-base font-extrabold text-[var(--color-ink)]">
                  خدمة العملاء
                </h2>
                <p className="mt-0.5 text-sm text-[var(--color-ink-soft)]">
                  كيف يمكننا مساعدتك؟
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={closeForm}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[var(--color-ink-soft)] outline-none hover:bg-white hover:text-[#ee376a] focus-visible:ring-2 focus-visible:ring-[#ee376a] focus-visible:ring-offset-2"
              aria-label="إغلاق خدمة العملاء"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>

        {!isAuthenticated ? (
          <SupportLoginPrompt onLoginClick={closeForm} />
        ) : submissionState === "success" ? (
          <SupportSuccess />
        ) : (
          <form className="space-y-4 p-4" onSubmit={handleSubmit} noValidate>
            <div>
              <label
                htmlFor="support-message"
                className="mb-2 block text-sm font-bold text-[var(--color-ink)]"
              >
                اكتب استفسارك
              </label>
              <textarea
                id="support-message"
                value={message}
                onChange={(event) => {
                  setMessage(event.target.value);
                  if (error) {
                    setError("");
                  }
                }}
                placeholder="اكتب استفسارك أو المشكلة التي تواجهها..."
                rows={5}
                disabled={submissionState === "loading"}
                aria-invalid={Boolean(error)}
                aria-describedby={error ? "support-message-error" : undefined}
                className="min-h-32 w-full resize-none rounded-2xl border border-[#ee376a]/14 bg-[#fffafb] px-3.5 py-3 text-sm leading-6 text-[var(--color-ink)] outline-none transition placeholder:text-[var(--color-ink-muted)] focus:border-[#ee376a] focus:bg-white focus:ring-4 focus:ring-[#ee376a]/12 disabled:cursor-not-allowed disabled:opacity-70"
              />
              {error ? (
                <p
                  id="support-message-error"
                  className="mt-2 text-xs font-bold text-[#d92d20]"
                  role="alert"
                >
                  {error}
                </p>
              ) : null}
            </div>

            <button
              type="submit"
              disabled={submissionState === "loading"}
              className="flex min-h-11 w-full items-center justify-center gap-2 rounded-2xl bg-[#ee376a] px-4 py-3 text-sm font-extrabold text-white shadow-[0_14px_28px_rgba(238,55,106,0.24)] outline-none hover:-translate-y-0.5 hover:bg-[#db285b] focus-visible:ring-2 focus-visible:ring-[#ee376a] focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-75"
            >
              {submissionState === "loading" ? (
                <>
                  <Loader2
                    className="h-4 w-4 animate-spin"
                    aria-hidden="true"
                  />
                  جاري فتح واتساب...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" aria-hidden="true" />
                  إرسال إلى خدمة العملاء
                </>
              )}
            </button>
          </form>
        )}
      </div>

      {!isOpen ? (
        <SupportBubble
          message={BUBBLE_MESSAGES[bubbleIndex]}
          isVisible={isBubbleVisible}
          onOpen={() => {
            setIsBubbleVisible(false);
            setIsOpen(true);
          }}
        />
      ) : null}

      <button
        type="button"
        onClick={() => {
          setIsOpen((current) => {
            const nextIsOpen = !current;

            if (nextIsOpen) {
              setIsBubbleVisible(false);
            }

            return nextIsOpen;
          });
          setError("");
          setSubmissionState("idle");
        }}
        className="support-robot-button group relative flex h-20 w-20 items-center justify-center rounded-full outline-none focus-visible:ring-2 focus-visible:ring-[#ee376a] focus-visible:ring-offset-4 sm:h-24 sm:w-24 lg:h-28 lg:w-28"
        aria-label={isOpen ? "إغلاق خدمة العملاء" : "فتح خدمة العملاء"}
        aria-expanded={isOpen}
      >
        <span
          className="support-wave absolute inset-2 rounded-full border border-[#ee376a]/24"
          aria-hidden="true"
        />
        <span
          className="absolute inset-3 rounded-full bg-[#ee376a]/10 blur-md transition group-hover:bg-[#ee376a]/16"
          aria-hidden="true"
        />
        <span className="support-robot relative block h-full w-full">
          <Image
            src={ROBOT_IMAGE_SRC}
            alt=""
            fill
            sizes="(max-width: 640px) 80px, (max-width: 1024px) 96px, 112px"
            className="object-contain "
            priority={false}
          />
        </span>
      </button>

      <style jsx global>{`
        @keyframes support-robot-enter {
          0% {
            opacity: 0;
            transform: translate3d(7rem, 0, 0) scale(0.94);
          }
          70% {
            opacity: 1;
            transform: translate3d(-0.35rem, 0, 0) scale(1.02);
          }
          100% {
            opacity: 1;
            transform: translate3d(0, 0, 0) scale(1);
          }
        }

        @keyframes support-robot-float {
          0%,
          100% {
            transform: translate3d(0, 0, 0);
          }
          50% {
            transform: translate3d(0, -8px, 0);
          }
        }

        @keyframes support-wave-pulse {
          0% {
            opacity: 0.42;
            transform: scale(0.82);
          }
          70% {
            opacity: 0;
            transform: scale(1.28);
          }
          100% {
            opacity: 0;
            transform: scale(1.28);
          }
        }

        @keyframes support-check-pop {
          0% {
            opacity: 0;
            transform: scale(0.72);
          }
          70% {
            opacity: 1;
            transform: scale(1.08);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }

        .support-robot-button {
          animation: support-robot-enter 900ms cubic-bezier(0.16, 1, 0.3, 1)
            both;
          will-change: transform, opacity;
        }

        .support-robot {
          animation: support-robot-float 4.8s ease-in-out infinite;
          will-change: transform;
        }

        .support-wave {
          animation: support-wave-pulse 3.8s ease-out infinite;
          will-change: transform, opacity;
        }

        .support-checkmark {
          animation: support-check-pop 360ms cubic-bezier(0.16, 1, 0.3, 1)
            both;
        }

        @media (prefers-reduced-motion: reduce) {
          .support-robot-button,
          .support-robot,
          .support-wave,
          .support-checkmark {
            animation: none !important;
          }
        }
      `}</style>
    </div>
  );
}
